import { viewIdKind } from "@gooner/shared";

/**
 * The unsaved views this browser has open recently.
 *
 * An unsaved view has no row — it lives in a server-side registry keyed by an id — so
 * `GET /api/views` cannot return it, and the views page had no way to show one. Building
 * a view signed out and pressing Back therefore lost it completely: the entry was still
 * alive on the server for another five minutes, but nothing on the page knew its id.
 *
 * This is that missing memory, and it is deliberately only a memory. It stores ids, never
 * view content — the server remains the only thing that knows what a view IS, so a stale
 * entry can never resurrect a wrong version of it. An id whose entry has expired simply
 * fails to resolve and is dropped.
 *
 * Per-browser rather than per-account on purpose: this is for the signed-out case, where
 * there is no account to hang it on.
 */
const KEY = "gooner.recentTempViews";
/** More than fits on the page is more than anyone is coming back to. Newest first. */
const MAX = 12;

function read(): string[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Only ever temporary ids. A saved view belongs in the library list, and letting a
    // saved id in here would show it twice.
    return parsed
      .filter((x): x is string => typeof x === "string")
      .filter((id) => viewIdKind(id) === "temp")
      .slice(0, MAX);
  } catch {
    // Unparseable or storage blocked (private mode, disabled). Losing the list is the
    // pre-existing behaviour, so it degrades to exactly what it replaced.
    return [];
  }
}

function write(ids: string[]): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(ids.slice(0, MAX)));
  } catch {
    /* storage full or blocked — the list is a convenience, never a source of truth */
  }
}

/** Remember an unsaved view, newest first. Re-remembering moves it to the front. */
export function rememberTempView(id: string): void {
  if (viewIdKind(id) !== "temp") return;
  write([id, ...read().filter((x) => x !== id)]);
}

/** Forget one — it expired, or it was promoted into the library and now has a row. */
export function forgetTempView(id: string): void {
  write(read().filter((x) => x !== id));
}

export function recentTempViews(): string[] {
  return read();
}
